/* Shared runtime: native Shopify consent, deferred product motion, and one playing video. */
(() => {
  'use strict';
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 989px)');
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
  const visible = node => Boolean(node.isConnected && node.getClientRects().length && !node.closest('[hidden]'));
  let consentPromise;
  function privacyAPI() {
    if (window.Shopify && Shopify.customerPrivacy) return Promise.resolve(Shopify.customerPrivacy);
    if (!consentPromise) consentPromise = new Promise((resolve, reject) => {
      if (!window.Shopify || !Shopify.loadFeatures) { reject(new Error('Privacy service is unavailable. Please try again.')); return; }
      const timeout = setTimeout(() => reject(new Error('Privacy service did not respond. Please try again.')), 12000);
      Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => {
        clearTimeout(timeout);
        if (error || !Shopify.customerPrivacy) reject(new Error('Privacy service is unavailable. Please try again.'));
        else resolve(Shopify.customerPrivacy);
      });
    }).catch(error => { consentPromise = null; throw error; });
    return consentPromise;
  }
  const gpc = () => navigator.globalPrivacyControl === true;
  let dialog, opener, scrollPosition, oldBodyStyle;
  function setBusy(busy) {
    if (!dialog) return;
    dialog.querySelector('[data-privacy-fields]').disabled = busy;
    dialog.querySelectorAll('[data-privacy-save]').forEach(button => { button.disabled = busy; });
    if (!busy) dialog.querySelector('[name=marketing]').disabled = gpc();
  }
  function syncConsent(api) {
    if (!dialog) return;
    const state = api.currentVisitorConsent();
    ['preferences','analytics','marketing'].forEach(key => { dialog.querySelector('[name='+key+']').checked = state[key] === 'yes' && !(key === 'marketing' && gpc()); });
    dialog.querySelector('[data-privacy-gpc]').hidden = !gpc();
    dialog.querySelector('[data-privacy-status]').textContent = '';
    setBusy(false);
  }
  function restorePage() {
    if (oldBodyStyle === undefined) return;
    if (oldBodyStyle === null) document.body.removeAttribute('style'); else document.body.setAttribute('style', oldBodyStyle);
    oldBodyStyle = undefined;
    const previous = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(scrollPosition.x, scrollPosition.y);
    document.documentElement.style.scrollBehavior = previous;
    if (opener && opener.isConnected) opener.focus({preventScroll:true});
  }
  async function openPrivacy(trigger) {
    dialog = document.getElementById('PrivacyDialog');
    if (!dialog || dialog.open) return;
    opener = trigger;
    scrollPosition = {x:scrollX,y:scrollY};
    const scrollbar = innerWidth - document.documentElement.clientWidth;
    oldBodyStyle = document.body.getAttribute('style');
    Object.assign(document.body.style,{position:'fixed',top:-scrollY+'px',left:'0',width:'100%',paddingRight:scrollbar+'px'});
    dialog.showModal();
    dialog.querySelector('[data-privacy-close]').focus({preventScroll:true});
    dialog.querySelector('[data-privacy-status]').textContent = 'Loading your saved preferences…';
    setBusy(true);
    try { syncConsent(await privacyAPI()); }
    catch (error) { dialog.querySelector('[data-privacy-status]').textContent = error.message; }
  }
  async function saveConsent(mode) {
    if (!dialog || dialog.querySelector('[data-privacy-fields]').disabled) return;
    const data = {};
    ['preferences','analytics','marketing'].forEach(key => { data[key] = mode === 'custom' ? dialog.querySelector('[name='+key+']').checked : mode === 'all'; });
    if (gpc()) data.marketing = false;
    data.sale_of_data = data.marketing && !gpc();
    setBusy(true);
    dialog.querySelector('[data-privacy-status]').textContent = 'Saving preferences…';
    try {
      const api = await privacyAPI();
      await new Promise((resolve,reject) => {
        const timer = setTimeout(() => reject(new Error('Preferences could not be saved. Please try again.')),12000);
        api.setTrackingConsent(data, result => { clearTimeout(timer); if (result && result.error) reject(new Error('Preferences could not be saved. Please try again.')); else resolve(); });
      });
      dialog.close();
    } catch (error) { dialog.querySelector('[data-privacy-status]').textContent = error.message; setBusy(false); }
  }
  document.addEventListener('click', event => {
    const open = event.target.closest('[data-privacy-open]');
    if (open) { openPrivacy(open); return; }
    if (event.target.closest('[data-privacy-close]')) { dialog?.close(); return; }
    const save = event.target.closest('[data-privacy-save]');
    if (save && save.type !== 'submit') saveConsent(save.dataset.privacySave);
  });
  document.addEventListener('submit', event => { if (event.target.matches('[data-privacy-form]')) { event.preventDefault(); saveConsent('custom'); } });
  document.addEventListener('close', event => { if (event.target.id === 'PrivacyDialog') restorePage(); }, true);
  document.addEventListener('keydown', event => {
    if (dialog?.open && event.key === 'Tab') window.CygnaxisA11y?.containTab(event,dialog);
  });
  document.addEventListener('visitorConsentCollected', () => { if (dialog?.open) privacyAPI().then(syncConsent).catch(()=>{}); });
  // Honor GPC through Shopify's single consent store; preserve unrelated consent choices.
  if (gpc()) privacyAPI().then(api => {
    const state = api.currentVisitorConsent();
    if (state.sale_of_data !== 'no' || state.marketing !== 'no') api.setTrackingConsent({sale_of_data:false,marketing:false},()=>{});
  }).catch(()=>{});

  function initializeMedia(scope) {
    scope.querySelectorAll('[data-feature-media]').forEach(root => {
      if (root.dataset.ready) return;
      root.dataset.ready = 'true';
      const controller = new AbortController(), {signal} = controller;
      if (root.dataset.kind === 'image') {
        const still = root.querySelector('.pdp-feature-media__static');
        still?.addEventListener('error', () => {
          const fallback = root.querySelector('[data-static-fallback]');
          if (fallback) { still.hidden = true; fallback.hidden = false; }
        }, {once:true, signal});
        root._disposeMedia = () => controller.abort();
        return;
      }
      const button = root.querySelector('[data-feature-toggle]');
      const template = root.querySelector('[data-feature-template]');
      if (!button || !template) return;
      const poster = root.querySelector('.pdp-feature-media__poster');
      const empty = root.querySelector('[data-video-empty]');
      const status = root.querySelector('[data-feature-status]');
      let media, userPaused = false, inView = false, wanted = false, providerReady = false, timer;
      const state = playing => {
        button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        button.setAttribute('aria-pressed', String(playing));
        if (poster) poster.hidden = playing;
        if (empty) empty.hidden = playing;
      };
      const post = message => {
        if (media?.tagName !== 'IFRAME') return;
        media.contentWindow?.postMessage(JSON.stringify(message), new URL(media.src).origin);
      };
      const command = action => {
        if (media?.dataset.provider === 'youtube') post({event:'command',func:action === 'play' ? 'playVideo' : 'pauseVideo',args:[]});
        else post({method:action});
      };
      const pause = () => {
        wanted = false; clearTimeout(timer);
        if (media?.tagName === 'VIDEO') media.pause();
        else if (media?.tagName === 'IFRAME') command('pause');
        else if (media?.tagName === 'IMG') media.hidden = true;
        state(false);
      };
      root._pauseMedia = pause;
      root._setMediaUserPaused = value => { userPaused = Boolean(value); };
      const failure = () => {
        pause();
        if (status) status.textContent = 'Playback did not start. Select Play to try again.';
      };
      const confirmPlayback = () => {
        if (!wanted || !visible(root) || document.hidden) { pause(); return; }
        clearTimeout(timer); state(true);
        if (status) status.textContent = '';
      };
      function hydrate() {
        if (media) return;
        media = template.content.firstElementChild?.cloneNode(true);
        if (!media) return;
        media.addEventListener('error', failure, {signal});
        if (media.tagName === 'VIDEO') {
          media.addEventListener('playing', confirmPlayback, {signal});
          media.addEventListener('pause', () => state(false), {signal});
        } else if (media.tagName === 'IMG') {
          media.addEventListener('load', confirmPlayback, {signal});
        } else if (media.tagName === 'IFRAME') {
          media.addEventListener('load', () => {
            if (media.dataset.provider === 'youtube') post({event:'listening',id:root.id || 'pdp-player'});
            else ['play','pause','error'].forEach(event => post({method:'addEventListener',value:event}));
          }, {signal});
        }
        if (media.tagName === 'IFRAME' || media.tagName === 'IMG') media.loading = 'eager';
        root.insertBefore(media, template);
      }
      const play = async user => {
        if (!visible(root) || document.hidden || (!user && (!inView || motion.matches || saveData() || userPaused || root.dataset.autoplay === 'false'))) return;
        wanted = true;
        document.querySelectorAll('[data-feature-media]').forEach(other => { if (other !== root) other._pauseMedia?.(); });
        hydrate();
        if (!media) { failure(); return; }
        clearTimeout(timer);
        timer = setTimeout(() => { if (button.getAttribute('aria-pressed') !== 'true') failure(); }, 10000);
        try {
          if (media.tagName === 'VIDEO') {
            window.CygnaxisMedia?.pauseOutside(media);
            await media.play();
            if (!wanted) media.pause();
          } else if (media.tagName === 'IMG') {
            media.hidden = false;
            if (media.complete && media.naturalWidth > 0) confirmPlayback();
          } else if (providerReady) command('play');
        } catch (_) { failure(); }
      };
      root._playMedia = play;
      root._restartMedia = () => {
        if (media?.tagName === 'VIDEO') media.currentTime = 0;
        else if (media?.tagName === 'IFRAME') {
          if (media.dataset.provider === 'youtube') post({event:'command',func:'seekTo',args:[0,true]});
          else post({method:'setCurrentTime',value:0});
        }
      };
      window.addEventListener('message', event => {
        if (media?.tagName !== 'IFRAME' || event.source !== media.contentWindow || event.origin !== new URL(media.src).origin) return;
        let data; try { data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch (_) { return; }
        if (!data) return;
        if (data.event === 'onReady' || data.event === 'ready') {
          providerReady = true;
          if (media.dataset.provider === 'youtube') {
            post({event:'command',func:'mute',args:[]});
            post({event:'command',func:'addEventListener',args:['onStateChange']});
            post({event:'command',func:'addEventListener',args:['onError']});
          } else ['play','pause','error'].forEach(value => post({method:'addEventListener',value}));
          command(wanted ? 'play' : 'pause');
        }
        if (data.event === 'play' || (data.event === 'onStateChange' && data.info === 1)) confirmPlayback();
        if (data.event === 'pause' || (data.event === 'onStateChange' && data.info === 2)) state(false);
        if (data.event === 'error' || data.event === 'onError') failure();
      }, {signal});
      button.addEventListener('click', () => {
        if (wanted) { userPaused = true; pause(); }
        else { userPaused = false; play(true); }
        root.dispatchEvent(new CustomEvent('cygnaxis:media-manual',{bubbles:true,detail:{paused:userPaused}}));
      }, {signal});
      root.addEventListener('click', event => {
        if (event.target.tagName === 'VIDEO' || event.target.tagName === 'IMG') button.click();
      }, {signal});
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        inView = entry.intersectionRatio >= .35;
        if (inView) play(false); else pause();
      }), {threshold:[0,.35]});
      observer.observe(root);
      motion.addEventListener('change', pause, {signal});
      mobile.addEventListener('change', pause, {signal});
      document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); }, {signal});
      root._disposeMedia = () => { pause(); observer.disconnect(); controller.abort(); };
    });
  }
  // Use the displayed poster for ambient fill; no second high-resolution download.
  function ambientImages(scope) {
    scope.querySelectorAll('.deferred-video__poster').forEach(img => {
      const sync = () => { const ambient = img.parentElement.querySelector('.deferred-video__ambient'); if(ambient && img.currentSrc) ambient.style.backgroundImage = 'url('+JSON.stringify(img.currentSrc)+')'; };
      if(img.complete) sync(); else img.addEventListener('load',sync,{once:true});
    });
  }
  document.addEventListener('play', event => {
    if (event.target.tagName !== 'VIDEO') return;
    if (!visible(event.target) || document.hidden) { event.target.pause(); return; }
    window.CygnaxisMedia?.pauseOutside(event.target);
  },true);
  initializeMedia(document); ambientImages(document);
  document.addEventListener('shopify:section:load', event => { initializeMedia(event.target);ambientImages(event.target); });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-feature-media]').forEach(root=>root._disposeMedia?.());
    if(event.target.contains(dialog)){dialog.close();restorePage();}
  });
})();
